import React from 'react';
import { useLocation } from 'react-router-dom';
import { ModuleLink as Link } from './ModuleLink';
import { useAuth } from '../context/AuthContext';
import { logoutToShell } from '../auth/authBridge';
import styles from './Nav.module.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faArrowRightFromBracket, faHouse } from '@fortawesome/free-solid-svg-icons';
import { LayoutDashboard } from 'lucide-react';
import dragnetLogo from '../assets/dragnet-logo.png';

const ERP_URL = import.meta.env.VITE_DRAGNET_ERP_URL ?? '';

export { styles as shellStyles };

export default function Nav() {
  const { userName, userEmail } = useAuth();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [userMenuOpen, setUserMenuOpen] = React.useState(false);
  const menuRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  React.useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setUserMenuOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  React.useEffect(() => {
    const handler = () => {
      if (window.innerWidth > 768) setMobileOpen(false);
    };
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  const displayName = userName || userEmail || 'User';
  const initials = displayName
    .split(' ')
    .map((p: string) => p.charAt(0))
    .join('')
    .slice(0, 2)
    .toUpperCase();

  return (
    <nav className={styles.navbar} role='navigation' aria-label='Main navigation'>
      {/* Brand */}
      <Link to='/' className={styles.navbarBrand} aria-label='Dragnet ERP — Change Management'>
        <img src={dragnetLogo} alt='Dragnet' className={styles.logoImg} />
        <div className={styles.logoWordmark}>
          <span className={styles.logoName}>Change Management</span>
        </div>
      </Link>

      {/* Desktop links */}
      <div className={styles.navLinks}>
        <a href={ERP_URL} className={styles.navItem}>
          <FontAwesomeIcon icon={faHouse} aria-hidden='true' />
          <span>Home</span>
        </a>
        <Link to='/' className={styles.navItem}>
          <LayoutDashboard size={14} aria-hidden='true' />
          <span>Dashboard</span>
        </Link>
      </div>

      {/* Right: user menu */}
      <div className={styles.navbarRight}>
        <div className={styles.userSection} ref={menuRef}>
          <button
            type='button'
            className={styles.userTrigger}
            onClick={() => setUserMenuOpen((p) => !p)}
            title={displayName}
            aria-label='User menu'
            aria-expanded={userMenuOpen}
          >
            <div className={styles.userAvatar}>{initials}</div>
            <FontAwesomeIcon icon={faChevronDown} className={styles.chevron} aria-hidden='true' />
          </button>

          {userMenuOpen && (
            <div className={styles.userMenu}>
              <div className={styles.userMenuHeader}>
                <div className={styles.userMenuAvatar}>{initials}</div>
                <div className={styles.userMenuMeta}>
                  <div className={styles.userMenuName}>{displayName}</div>
                  <div className={styles.userMenuEmail}>{userEmail}</div>
                </div>
              </div>
              <div className={styles.userMenuDivider} />
              <button
                type='button'
                className={`${styles.userMenuBtn} ${styles.danger}`}
                onClick={logoutToShell}
              >
                <FontAwesomeIcon icon={faArrowRightFromBracket} aria-hidden='true' />
                <span>Sign Out</span>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Hamburger */}
      <button
        type='button'
        className={[styles.hamburger, mobileOpen ? styles.hamburgerActive : '']
          .filter(Boolean)
          .join(' ')}
        onClick={() => setMobileOpen((p) => !p)}
        aria-label='Toggle navigation'
        aria-expanded={mobileOpen}
      >
        <span />
        <span />
        <span />
      </button>

      {/* Mobile panel */}
      {mobileOpen && (
        <>
          <div className={styles.mobileOverlay} onClick={() => setMobileOpen(false)} />
          <nav className={styles.mobileNav}>
            <a href={ERP_URL} className={styles.mobileNavItem}>
              <span>Dragnet ERP</span>
            </a>
            <Link to='/' className={styles.mobileNavItem} onClick={() => setMobileOpen(false)}>
              <LayoutDashboard size={14} aria-hidden='true' />
              <span>Dashboard</span>
            </Link>
            <div className={styles.mobileNavDivider} />
            <button
              type='button'
              className={`${styles.mobileNavItem} ${styles.mobileNavDanger}`}
              onClick={logoutToShell}
            >
              <FontAwesomeIcon icon={faArrowRightFromBracket} aria-hidden='true' />
              <span>Sign Out</span>
            </button>
          </nav>
        </>
      )}
    </nav>
  );
}
